import type { FunctionComponent } from 'react';
import { useCallback, useContext } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { forTemplate, localizeCount } from '@trello/legacy-i18n';
import { asMoney } from '@trello/legacy-i18n/formatters';
import { Button } from '@trello/nachos/button';
import type { BillingIds, FreeTrialTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { WithEnterpriseManagedOverride } from 'app/src/components/UpgradePrompts';
import { PlanComparisonContext } from '../PlanComparisonContext';
import type { BillingQuotes } from '../types';

import styles from './PlanComparisonPremiumHeaderCell.less';

const format = forTemplate('billing_page_one');

export interface PlanComparisonPremiumHeaderCellProps {
  teamId: string;
  teamName: string;
  isMonthly: boolean;
  // Business class is a legacy name for the premium plan!
  prices?: BillingQuotes['businessPrices'];
  onShowFreeTrialModal: (planName: string) => void;
  isEligibleForTrial?: boolean;
  isInPPCohort?: boolean;
}

export const PlanComparisonPremiumHeaderCell: FunctionComponent<
  PlanComparisonPremiumHeaderCellProps
> = ({
  teamId,
  isMonthly,
  prices,
  onShowFreeTrialModal,
  isEligibleForTrial,
  isInPPCohort,
}) => {
  const { variation, onChoosePremium } = useContext(PlanComparisonContext);

  const productCode = isMonthly
    ? prices?.monthly.productCode
    : prices?.annual.productCode;
  const pricePerUser = isMonthly
    ? prices?.monthly.subTotalPerUser
    : prices?.annual.subTotalPerUser;

  const onKeepPremiumWithAnalytics = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'choosePremiumButton',
      source: 'trelloPlanPricingModal',
      attributes: {
        viaEndOfTrial: true,
      },
    });
    onChoosePremium?.(productCode!);
  }, [onChoosePremium, productCode]);

  const onClickFreeTrialButton = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'startBCFreeTrialButton',
      source: 'planComparisonSection',
      containers: {
        organization: {
          id: teamId,
        },
      },
      attributes: {
        eligibleForFreeTrial: isEligibleForTrial,
      },
    });
    onShowFreeTrialModal('business class');
  };

  const onClickUpgradeButton = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'upgradeToBCButton',
      source: 'planComparisonSection',
      containers: {
        organization: {
          id: teamId,
        },
      },
    });
    onChoosePremium?.(productCode!);
  }, [onChoosePremium, productCode, teamId]);

  let actionPrompt = null;
  switch (variation) {
    case 'reverse-trial-overlay':
      actionPrompt = (
        <Button appearance="primary" onClick={onClickUpgradeButton}>
          {format('upgrade-now')}
        </Button>
      );
      break;
    case 'end-of-trial-friction-overlay':
      actionPrompt = (
        <Button appearance="primary" onClick={onKeepPremiumWithAnalytics}>
          {'Keep Premium'}
        </Button>
      );
      break;
    case 'downgrade-plan-overlay':
      actionPrompt = (
        <section className={styles.currentPlan}>
          <div>{format('current-plan')}</div>
          <div>({format(isMonthly ? 'paid-monthly' : 'paid-yearly')})</div>
        </section>
      );
      break;
    case 'billing-page':
    default:
      actionPrompt = isEligibleForTrial ? (
        <>
          <Button
            key="free-trial-business-class"
            onClick={onClickFreeTrialButton}
            appearance="primary"
            testId={getTestId<FreeTrialTestIds>('active-free-trial-banner')}
          >
            <WithEnterpriseManagedOverride>
              {localizeCount('try free', 14)}
            </WithEnterpriseManagedOverride>
          </Button>
          <p className={styles.purchase}>
            {format('or', {
              purchaseNow: (
                <Button
                  key={`upgrade-business-class`}
                  testId={getTestId<BillingIds>('upgrade-team-bc-button')}
                  appearance="link"
                  onClick={onClickUpgradeButton}
                >
                  {format('upgrade-now-lower-case')}
                </Button>
              ),
            })}
          </p>
        </>
      ) : (
        <Button
          key="upgrade-business-class"
          onClick={onClickUpgradeButton}
          appearance="primary"
          testId={getTestId<BillingIds>('upgrade-team-button')}
        >
          {format('upgrade-now')}
        </Button>
      );
  }

  return (
    <th
      className={styles.planCell}
      data-value={format('best-value')}
      data-testid={getTestId<BillingIds>('plan-comparison-cell-premium')}
    >
      <h3 className={styles.name}>{format('business-class-name')}</h3>
      <p className={styles.info}>
        {isInPPCohort
          ? format('premium-description-personal-productivity')
          : format('premium-description-repackaging-gtm')}
      </p>
      <span className={styles.price}>
        {format('amount-usd', {
          amount: asMoney(pricePerUser || 0).replace(/\.00$/, ''),
        }).replace(/\s/g, ' ')}
      </span>
      <p className={styles.frequency}>
        {format('per-user')}
        <br />
        {format(isMonthly ? 'per-month' : 'per-year')}
      </p>
      {actionPrompt}
    </th>
  );
};
